import { createContext, useContext, useReducer } from "@wordpress/element";
import { DEFAULT_SETTINGS } from "../constants/defaultSettings";

const Ctx = createContext(null);

const HISTORY_LIMIT = 50;

function snapshot(state) {
    return { settings: state.settings, title: state.title };
}

function reducer(state, action) {
    switch (action.type) {
        case "LOAD":
            return {
                ...state,
                title: action.title ?? state.title,
                settings: { ...DEFAULT_SETTINGS, ...action.data },
                loaded: true,
                isDirty: false,
                past: [],
                future: [],
            };
        case "SET": {
            const past = [...state.past, snapshot(state)].slice(-HISTORY_LIMIT);
            return {
                ...state,
                past,
                future: [],
                isDirty: true,
                settings: { ...state.settings, [action.key]: action.value },
            };
        }
        case "APPLY_LAYOUT": {
            const past = [...state.past, snapshot(state)].slice(-HISTORY_LIMIT);
            const merged = { ...state.settings, ...action.config };
            // Enforce constraints for gallery types that only support a subset
            // of position values — prevents a stale saved value from being
            // preserved when galleryType is overridden by the layout config.
            if (merged.galleryType === "anchor") {
                if (!["left", "right"].includes(merged.thumbnailPosition)) {
                    merged.thumbnailPosition = "left";
                }
                if (!["left", "right"].includes(merged.mainImagePaginationPosition)) {
                    merged.mainImagePaginationPosition = "left";
                }
                if (merged.mainImagePaginationType !== "dots") {
                    merged.mainImagePaginationType = "dots";
                }
            }
            return {
                ...state,
                past,
                future: [],
                isDirty: true,
                settings: merged,
            };
        }
        case "SET_TITLE": {
            const past = [...state.past, snapshot(state)].slice(-HISTORY_LIMIT);
            return {
                ...state,
                past,
                future: [],
                isDirty: true,
                title: action.title,
            };
        }
        case "UNDO": {
            if (state.past.length === 0) return state;
            const prev = state.past[state.past.length - 1];
            const past = state.past.slice(0, -1);
            const future = [snapshot(state), ...state.future].slice(
                0,
                HISTORY_LIMIT,
            );
            return {
                ...state,
                past,
                future,
                settings: prev.settings,
                title: prev.title,
                isDirty: true,
            };
        }
        case "REDO": {
            if (state.future.length === 0) return state;
            const next = state.future[0];
            const future = state.future.slice(1);
            const past = [...state.past, snapshot(state)].slice(-HISTORY_LIMIT);
            return {
                ...state,
                past,
                future,
                settings: next.settings,
                title: next.title,
                isDirty: true,
            };
        }
        case "SET_TAB":
            return { ...state, activeTab: action.tab };
        case "SET_GALLERY_SUB_TAB":
            return { ...state, gallerySubTab: action.subTab };
        case "SAVING":
            return { ...state, saving: true, saved: false, error: null };
        case "SAVED":
            return { ...state, saving: false, saved: true, isDirty: false };
        case "ERROR":
            return { ...state, saving: false, error: action.msg };
        case "CLEAR_SAVED":
            return { ...state, saved: false };
        default:
            return state;
    }
}

export function SettingsProvider({ children }) {
    const [state, dispatch] = useReducer(reducer, {
        title: "",
        settings: { ...DEFAULT_SETTINGS },
        activeTab: "general",
        gallerySubTab: "general",
        loaded: false,
        saving: false,
        saved: false,
        error: null,
        isDirty: false,
        past: [],
        future: [],
    });

    const actions = {
        load: (data, title) => dispatch({ type: "LOAD", data, title }),
        set: (key, value) => dispatch({ type: "SET", key, value }),
        applyLayout: (config) => dispatch({ type: "APPLY_LAYOUT", config }),
        setTitle: (title) => dispatch({ type: "SET_TITLE", title }),
        setTab: (tab) => dispatch({ type: "SET_TAB", tab }),
        setGallerySubTab: (subTab) =>
            dispatch({ type: "SET_GALLERY_SUB_TAB", subTab }),
        undo: () => dispatch({ type: "UNDO" }),
        redo: () => dispatch({ type: "REDO" }),
        setSaving: () => dispatch({ type: "SAVING" }),
        setSaved: () => dispatch({ type: "SAVED" }),
        setError: (msg) => dispatch({ type: "ERROR", msg }),
        clearSaved: () => dispatch({ type: "CLEAR_SAVED" }),
    };

    return <Ctx.Provider value={{ state, actions }}>{children}</Ctx.Provider>;
}

export function useSettings() {
    return useContext(Ctx);
}
